import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TechnicianDispatch = ({ workOrders, technicians, equipment, onRefresh }) => {
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  const pendingWorkOrders = workOrders?.filter(wo => wo?.status === 'pending');

  const getMatchingTechnicians = (workOrder) => {
    const equipmentItem = equipment?.find(eq => eq?.id === workOrder?.equipment_id);
    if (!equipmentItem) return technicians;

    // Match technicians based on equipment category and skills
    return technicians?.map(tech => {
      const skillMatch = tech?.skills?.some(skill => 
        equipmentItem?.category?.toLowerCase()?.includes(skill?.toLowerCase())
      );
      const availabilityScore = tech?.availability === 'available' ? 3 : tech?.availability === 'busy' ? 1 : 0;
      const workloadScore = 6 - tech?.workload;
      const matchScore = (skillMatch ? 10 : 0) + availabilityScore + workloadScore;

      return {
        ...tech,
        matchScore,
        skillMatch
      };
    })?.sort((a, b) => b?.matchScore - a?.matchScore);
  };

  const getAvailabilityBadge = (availability) => {
    const config = {
      available: { label: 'Available', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle2' },
      busy: { label: 'Busy', color: 'text-warning', bg: 'bg-warning/10', icon: 'Clock' },
      'on-break': { label: 'On Break', color: 'text-muted-foreground', bg: 'bg-muted', icon: 'Coffee' },
      unavailable: { label: 'Unavailable', color: 'text-error', bg: 'bg-error/10', icon: 'XCircle' }
    };
    const cfg = config?.[availability] || config?.available;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
        <Icon name={cfg?.icon} size={12} />
        {cfg?.label}
      </span>
    );
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'border-error bg-error/5',
      high: 'border-warning bg-warning/5',
      medium: 'border-primary bg-primary/5',
      low: 'border-border bg-card'
    };
    return colors?.[priority] || colors?.medium;
  };

  return (
    <div className="space-y-6">
      {/* Dispatch Queue */}
      <div className="bg-card rounded-xl border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Send" size={20} />
          Dispatch Queue ({pendingWorkOrders?.length})
        </h3>

        <div className="space-y-4">
          {pendingWorkOrders?.map((wo) => {
            const equipmentItem = equipment?.find(eq => eq?.id === wo?.equipment_id);
            const matchingTechs = getMatchingTechnicians(wo);
            const isSelected = selectedWorkOrder?.id === wo?.id;

            return (
              <div key={wo?.id} className="space-y-4">
                <div
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    isSelected ? 'border-primary bg-primary/5' : getPriorityColor(wo?.priority)
                  }`}
                  onClick={() => setSelectedWorkOrder(isSelected ? null : wo)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-foreground">{wo?.title}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          wo?.priority === 'urgent' ? 'bg-error/10 text-error' :
                          wo?.priority === 'high'? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'
                        }`}>
                          {wo?.priority}
                        </span>
                      </div>
                      {equipmentItem && (
                        <p className="text-sm text-muted-foreground">
                          <Icon name="Package" size={14} className="inline mr-1" />
                          {equipmentItem?.name} - {equipmentItem?.location}
                        </p>
                      )}
                    </div>
                    <Icon
                      name={isSelected ? 'ChevronUp' : 'ChevronDown'}
                      size={20}
                      className="text-muted-foreground"
                    />
                  </div>
                </div>

                {/* Technician Recommendations */}
                {isSelected && (
                  <div className="ml-4 space-y-3">
                    <p className="text-sm font-medium text-foreground">Recommended Technicians</p>
                    {matchingTechs?.slice(0, 3)?.map((tech, index) => (
                      <div
                        key={tech?.id}
                        className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Icon name="User" size={20} className="text-primary" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-medium text-foreground">{tech?.name}</h5>
                                {index === 0 && tech?.skillMatch && (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent">
                                    Best Match
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>
                                  <Icon name="MapPin" size={12} className="inline mr-1" />
                                  {tech?.location}
                                </span>
                                <span>
                                  <Icon name="Briefcase" size={12} className="inline mr-1" />
                                  {tech?.workload} active tasks
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {tech?.skills?.map(skill => (
                                  <span
                                    key={skill}
                                    className="px-2 py-0.5 rounded-full text-xs bg-muted text-foreground"
                                  >
                                    {skill}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {getAvailabilityBadge(tech?.availability)}
                            <Button
                              variant="primary"
                              size="sm"
                              iconName="Send"
                              iconPosition="left"
                              disabled={tech?.availability === 'unavailable'}
                            >
                              Assign
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {pendingWorkOrders?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="CheckCircle2" size={48} className="mx-auto mb-4 text-success" />
            <h3 className="text-lg font-semibold text-foreground mb-2">All Clear!</h3>
            <p className="text-muted-foreground">No pending work orders to dispatch</p>
          </div>
        )}
      </div>

      {/* Active Technicians Map */}
      <div className="bg-card rounded-xl border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="MapPin" size={20} />
          Technician Locations
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {technicians?.map((tech) => (
            <div key={tech?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="User" size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{tech?.name}</h4>
                  <p className="text-xs text-muted-foreground">{tech?.location}</p>
                </div>
              </div>
              {getAvailabilityBadge(tech?.availability)}
              <div className="mt-3 text-sm text-muted-foreground">
                <p>Active tasks: {tech?.workload}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TechnicianDispatch;